import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { useConfirm } from "./ConfirmDialog";
import { AttachmentList, AttachmentComposer } from "./Attachments";

const EDIT_WINDOW_MS = 15 * 60 * 1000;

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function authorBadge(actor) {
  return (
    <span className={`disc-avatar disc-avatar-${actor === "MH" ? "mh" : "wh"}`} aria-hidden="true">
      {actor}
    </span>
  );
}

// ===== Topic index =====
function TopicIndex({ topics, onOpen, isAdmin, onCreate, onDelete, onToggleFav, currentActor }) {
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const topic = await onCreate({ title: title.trim(), body: body.trim() });
      setTitle("");
      setBody("");
      setComposing(false);
      if (topic) onOpen(topic.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="disc-index">
      <div className="disc-index-head">
        <div>
          <div className="sec-head" style={{ margin: 0 }}>Discussion</div>
          <div className="sec-desc">
            Open-ended strategy threads. Anyone can start a topic, anyone can reply.
          </div>
        </div>
        {!composing && (
          <button
            className="btn btn-primary"
            onClick={() => setComposing(true)}
            data-testid="disc-new-topic"
          >
            + New topic
          </button>
        )}
      </div>

      {composing && (
        <div className="disc-composer" data-testid="disc-new-topic-composer">
          <input
            type="text"
            className="disc-input"
            placeholder="Topic title (e.g. 'Should we pilot AI search with two existing clients first?')"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            data-testid="disc-new-topic-title"
            autoFocus
          />
          <textarea
            className="disc-textarea"
            placeholder="Optional first post — context, links, the question you want chewed on."
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="disc-new-topic-body"
          />
          <div className="disc-composer-actions">
            <button
              className="btn btn-primary"
              disabled={busy || !title.trim()}
              onClick={submit}
              data-testid="disc-new-topic-submit"
            >
              {busy ? "Posting…" : "Post topic"}
            </button>
            <button
              className="btn"
              onClick={() => {
                setComposing(false);
                setTitle("");
                setBody("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {topics.length === 0 ? (
        <div className="disc-empty">No topics yet. Start the first one.</div>
      ) : (
        <div className="disc-list">
          {topics.map((t) => {
            const fav = t.favoritedBy || [];
            const iStarred = fav.includes(currentActor);
            return (
            <button
              key={t.id}
              type="button"
              className="disc-row"
              onClick={() => onOpen(t.id)}
              data-testid={`disc-topic-${t.id}`}
            >
              {authorBadge(t.author)}
              <div className="disc-row-body">
                <div className="disc-row-title">{t.title}</div>
                <div className="disc-row-meta">
                  <span className="disc-row-meta-who">{t.authorName || t.author}</span>
                  <span className="disc-sep">·</span>
                  <span>started {relTime(t.createdAt)}</span>
                  <span className="disc-sep">·</span>
                  <span>
                    {t.commentCount} {t.commentCount === 1 ? "comment" : "comments"}
                  </span>
                  {t.commentFavoritesCount > 0 && (
                    <>
                      <span className="disc-sep">·</span>
                      <span className="disc-row-stars">{t.commentFavoritesCount} reply ★</span>
                    </>
                  )}
                  {t.lastAt && t.lastAuthorName && (
                    <>
                      <span className="disc-sep">·</span>
                      <span className="disc-row-last">
                        last by {t.lastAuthorName} {relTime(t.lastAt)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <span
                className={`disc-star ${iStarred ? "active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFav(t.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    onToggleFav(t.id);
                  }
                }}
                data-testid={`disc-topic-${t.id}-star`}
                title={iStarred ? "Unstar topic" : "Star topic"}
              >
                {iStarred ? "★" : "☆"}
                {fav.length > 0 && <span className="disc-star-count">{fav.length}</span>}
              </span>
              {isAdmin && (
                <span
                  className="disc-row-delete"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(t.id, t.title);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                      onDelete(t.id, t.title);
                    }
                  }}
                  data-testid={`disc-topic-${t.id}-delete`}
                  title="Delete topic"
                >
                  ×
                </span>
              )}
              <span className="disc-arrow">→</span>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== Single comment in thread view =====
function CommentItem({ topicId, comment, currentActor, isAdmin, onChange, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const isMine = comment.author === currentActor;
  const canEdit = isMine && Date.now() - new Date(comment.at).getTime() < EDIT_WINDOW_MS;
  const fav = comment.favoritedBy || [];
  const iStarred = fav.includes(currentActor);

  const saveEdit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/discussion/topics/${topicId}/comments/${comment.id}`,
        { body: draft.trim() }
      );
      onChange(data);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const toggleFav = async () => {
    const { data } = await api.post(
      `/discussion/topics/${topicId}/comments/${comment.id}/favorite`
    );
    onChange(data);
  };

  // Upload any pending files attached after the comment is posted (rare path —
  // composer below handles the create-time path).
  useEffect(() => {
    if (pendingFiles.length === 0) return;
    (async () => {
      setBusy(true);
      try {
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(
            `/discussion/topics/${topicId}/comments/${comment.id}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        }
        // Re-fetch the parent topic to refresh attachments — caller handles it via the
        // attachments deletion path; for simplicity we ask the parent to refresh.
        setPendingFiles([]);
        onChange({ ...comment, _needsRefresh: true });
      } finally {
        setBusy(false);
      }
    })();
  }, [pendingFiles.length]);

  return (
    <div
      className={`disc-comment ${iStarred ? "starred" : ""}`}
      id={`disc-comment-${comment.id}`}
      data-testid={`disc-comment-${comment.id}`}
    >
      {authorBadge(comment.author)}
      <div className="disc-comment-body">
        <div className="disc-comment-meta">
          <span className="disc-comment-who">{comment.authorName || comment.author}</span>
          <span className="disc-sep">·</span>
          <span>{relTime(comment.at)}</span>
          {comment.editedAt && (
            <>
              <span className="disc-sep">·</span>
              <span className="disc-comment-edited">edited</span>
            </>
          )}
        </div>

        {editing ? (
          <div className="disc-edit">
            <textarea
              className="disc-textarea"
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid={`disc-comment-${comment.id}-edit-input`}
            />
            <div className="disc-composer-actions">
              <button
                className="btn btn-primary"
                disabled={busy || !draft.trim()}
                onClick={saveEdit}
                data-testid={`disc-comment-${comment.id}-edit-save`}
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button className="btn" onClick={() => { setEditing(false); setDraft(comment.body); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="disc-comment-text">{comment.body}</div>
            {(comment.links || []).length > 0 && (
              <div className="cmt-links" data-testid={`disc-comment-${comment.id}-links`}>
                {(comment.links || []).map((l) => (
                  <a
                    key={l.id}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cmt-link-chip"
                  >
                    🔗 {l.url}
                  </a>
                ))}
              </div>
            )}
            <AttachmentList
              items={comment.attachments}
              canDelete={false}
              onDelete={() => {}}
              testIdPrefix={`disc-comment-${comment.id}-att`}
            />
          </>
        )}

        {fav.length > 0 && !editing && (
          <div className="disc-comment-favorited" data-testid={`disc-comment-${comment.id}-favorited-by`}>
            Favorited by {fav.map((a) => (a === "MH" ? "Marco" : "Ward")).join(", ")}
          </div>
        )}
      </div>

      <div className="disc-comment-actions">
        <button
          type="button"
          className={`disc-star ${iStarred ? "active" : ""}`}
          onClick={toggleFav}
          title={iStarred ? "Remove favorite" : "Favorite this comment"}
          data-testid={`disc-comment-${comment.id}-star`}
        >
          {iStarred ? "★" : "☆"} {fav.length > 0 && <span className="disc-star-count">{fav.length}</span>}
        </button>
        {canEdit && !editing && (
          <button
            type="button"
            className="disc-mini-btn"
            onClick={() => setEditing(true)}
            data-testid={`disc-comment-${comment.id}-edit`}
          >
            Edit
          </button>
        )}
        {isAdmin && !editing && (
          <button
            type="button"
            className="disc-mini-btn disc-mini-btn-danger"
            onClick={() => onDelete(comment.id)}
            data-testid={`disc-comment-${comment.id}-delete`}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ===== Topic thread view =====
function TopicThread({ tid, currentActor, isAdmin, onBack, focusCommentId }) {
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newBody, setNewBody] = useState("");
  const [newLinks, setNewLinks] = useState([]); // strings
  const [linkInput, setLinkInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const confirm = useConfirm();
  const MAX_LINKS = 5;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/discussion/topics/${tid}`);
      setTopic(data);
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    load();
  }, [load]);

  // Scroll to and flash-highlight a specific comment after it loads (deep-link from activity feed)
  useEffect(() => {
    if (!focusCommentId || !topic) return;
    setTimeout(() => {
      const el = document.getElementById(`disc-comment-${focusCommentId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("home-block-flash");
        setTimeout(() => el.classList.remove("home-block-flash"), 1600);
      }
    }, 120);
  }, [focusCommentId, topic]);

  const addPendingLink = () => {
    const url = linkInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) return;
    if (newLinks.length >= MAX_LINKS) return;
    if (newLinks.includes(url)) {
      setLinkInput("");
      return;
    }
    setNewLinks([...newLinks, url]);
    setLinkInput("");
  };

  const removePendingLink = (i) => setNewLinks(newLinks.filter((_, idx) => idx !== i));

  const submitComment = async () => {
    if (!newBody.trim() && newLinks.length === 0 && pendingFiles.length === 0) return;
    setPosting(true);
    try {
      const { data: cmt } = await api.post(`/discussion/topics/${tid}/comments`, {
        body: newBody.trim(),
        links: newLinks,
      });
      // Upload any attachments now that we have a comment id
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(
          `/discussion/topics/${tid}/comments/${cmt.id}/attachments`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      setNewBody("");
      setNewLinks([]);
      setPendingFiles([]);
      await load();
    } finally {
      setPosting(false);
    }
  };

  const replaceComment = (updated) => {
    if (!updated || updated._needsRefresh) {
      load();
      return;
    }
    setTopic((t) => ({
      ...t,
      comments: (t.comments || []).map((c) => (c.id === updated.id ? updated : c)),
    }));
  };

  const deleteComment = async (cid) => {
    const ok = await confirm({
      title: "Delete comment?",
      body: "This will permanently remove the comment.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/discussion/topics/${tid}/comments/${cid}`);
    await load();
  };

  if (loading) return <div className="disc-empty">Loading…</div>;
  if (!topic) return <div className="disc-empty">Topic not found.</div>;

  const comments = topic.comments || [];
  return (
    <div data-testid={`disc-thread-${tid}`}>
      <div className="crumb" style={{ marginBottom: 14 }}>
        <a onClick={onBack} data-testid="disc-back-to-index">← All topics</a>
      </div>

      <div className="disc-topic-header">
        <div className="disc-topic-header-row">
          {authorBadge(topic.author)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="disc-topic-title" data-testid="disc-thread-title">{topic.title}</h2>
            <div className="disc-row-meta" style={{ marginTop: 4 }}>
              <span className="disc-row-meta-who">{topic.authorName || topic.author}</span>
              <span className="disc-sep">·</span>
              <span>started {relTime(topic.createdAt)}</span>
              {(topic.favoritedBy || []).length > 0 && (
                <>
                  <span className="disc-sep">·</span>
                  <span className="disc-row-stars">
                    Starred by {(topic.favoritedBy || []).map((a) => (a === "MH" ? "Marco" : "Ward")).join(", ")}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            className={`disc-star ${(topic.favoritedBy || []).includes(currentActor) ? "active" : ""}`}
            onClick={async () => {
              const { data } = await api.post(`/discussion/topics/${tid}/favorite`);
              setTopic((t) => ({ ...t, favoritedBy: data.favoritedBy }));
            }}
            data-testid="disc-thread-star"
            title={(topic.favoritedBy || []).includes(currentActor) ? "Unstar topic" : "Star topic"}
          >
            {(topic.favoritedBy || []).includes(currentActor) ? "★" : "☆"}
            {(topic.favoritedBy || []).length > 0 && (
              <span className="disc-star-count">{(topic.favoritedBy || []).length}</span>
            )}
          </button>
        </div>
        {topic.body && <div className="disc-topic-body">{topic.body}</div>}
      </div>

      <div className="disc-divider">
        {comments.length === 0
          ? "No replies yet. Start the conversation."
          : `${comments.length} ${comments.length === 1 ? "reply" : "replies"}`}
      </div>

      <div className="disc-comments">
        {comments.map((c) => (
          <CommentItem
            key={c.id}
            topicId={tid}
            comment={c}
            currentActor={currentActor}
            isAdmin={isAdmin}
            onChange={replaceComment}
            onDelete={deleteComment}
          />
        ))}
      </div>

      <div className="disc-composer disc-reply-composer" data-testid="disc-reply-composer">
        <textarea
          className="disc-textarea"
          placeholder="Add a reply…"
          rows={5}
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          data-testid="disc-reply-body"
        />
        <AttachmentComposer
          pending={pendingFiles}
          setPending={setPendingFiles}
          testIdPrefix="disc-reply-att"
        />
        <div className="link-composer">
          {newLinks.length > 0 && (
            <div className="att-pending" style={{ marginBottom: 6 }}>
              {newLinks.map((l, i) => (
                <span className="att-chip att-chip-pending" key={`${l}-${i}`}>
                  <span>🔗</span>
                  <span className="att-name">{l}</span>
                  <button
                    type="button"
                    onClick={() => removePendingLink(i)}
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          {newLinks.length < MAX_LINKS && (
            <div className="link-add-row">
              <input
                className="link-add-input"
                type="url"
                placeholder={`Add link ${newLinks.length + 1} of ${MAX_LINKS} (https://…)`}
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPendingLink();
                  }
                }}
                data-testid="disc-reply-link-input"
              />
              <button
                type="button"
                className="btn btn-sm"
                onClick={addPendingLink}
                disabled={!linkInput.trim()}
                data-testid="disc-reply-link-add"
              >
                🔗 Add link
              </button>
            </div>
          )}
        </div>
        <div className="disc-composer-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={posting || (!newBody.trim() && newLinks.length === 0 && pendingFiles.length === 0)}
            onClick={submitComment}
            data-testid="disc-reply-submit"
          >
            {posting ? "Posting…" : "Post reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Top-level panel =====
export default function DiscussionPanel({ isAdmin, pendingFocus, clearPendingFocus }) {
  const { user } = useAuth();
  const currentActor = user?.role === "admin" ? "MH" : "WH";
  const confirm = useConfirm();
  const [topics, setTopics] = useState([]);
  const [openTid, setOpenTid] = useState(null);
  const [focusCommentId, setFocusCommentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/discussion/topics");
      setTopics(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Honor deep-link from activity feed: { focus: { kind: "discussion", id, commentId } }
  useEffect(() => {
    if (!pendingFocus) return;
    const f = pendingFocus.focus || {};
    if (f.kind === "discussion" && f.id) {
      setOpenTid(f.id);
      setFocusCommentId(f.commentId || null);
    }
    clearPendingFocus?.();
  }, [pendingFocus]);

  const createTopic = async (payload) => {
    const { data } = await api.post("/discussion/topics", payload);
    await refresh();
    return data;
  };

  const toggleTopicFav = async (tid) => {
    // Optimistic UI: flip the star locally, then re-sync from server.
    setTopics((items) =>
      items.map((t) =>
        t.id === tid
          ? {
              ...t,
              favoritedBy: (t.favoritedBy || []).includes(currentActor)
                ? (t.favoritedBy || []).filter((a) => a !== currentActor)
                : [...(t.favoritedBy || []), currentActor],
            }
          : t
      )
    );
    try {
      await api.post(`/discussion/topics/${tid}/favorite`);
    } finally {
      await refresh();
    }
  };

  const deleteTopic = async (tid, title) => {
    const ok = await confirm({
      title: `Delete "${title}"?`,
      body: "This will remove the topic and all its comments. This cannot be undone.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/discussion/topics/${tid}`);
    if (openTid === tid) setOpenTid(null);
    await refresh();
  };

  if (loading) return <div className="disc-empty">Loading…</div>;

  if (openTid) {
    return (
      <TopicThread
        tid={openTid}
        currentActor={currentActor}
        isAdmin={isAdmin}
        onBack={() => {
          setOpenTid(null);
          setFocusCommentId(null);
          refresh();
        }}
        focusCommentId={focusCommentId}
      />
    );
  }

  return (
    <TopicIndex
      topics={topics}
      onOpen={(tid) => setOpenTid(tid)}
      isAdmin={isAdmin}
      onCreate={createTopic}
      onDelete={deleteTopic}
      onToggleFav={toggleTopicFav}
      currentActor={currentActor}
    />
  );
}
